import React, { useState } from 'react';
import { Input, Button, Card } from 'antd';
import { RxDragHandleDots1 } from 'react-icons/rx';
import style from './style.module.scss';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';

import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';

interface Step {
  ["@id"]?: string;
  id: string;
  locationLabel: string;
}

interface Props {
  initialValue: any;
  onChange: (steps: any[]) => void;
}

const SortableStep = ({ id, index, locationLabel, onChange, onDelete }: {
  id: string;
  index: number;
  locationLabel: string;
  onChange: (val: string) => void;
  onDelete: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const customStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 8,
  };

  return (
    <div ref={setNodeRef} style={customStyle}>
      <Card size="small">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            backgroundColor: '#1677ff',
            color: '#fff',
            borderRadius: '50%',
            width: 24,
            height: 24,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 12,
          }}> {index + 1} </span>

          <Input
            placeholder="Label de lieu"
            value={locationLabel}
            onChange={(e) => onChange(e.target.value)}
            style={{ flex: 1 }}
          />

          <Button danger onClick={onDelete}>
            Supprimer
          </Button>

          <div
            {...attributes}
            {...listeners}
            style={{ cursor: 'grab', fontSize: 24, color: '#999' }}
          >
            <RxDragHandleDots1 />
          </div>
        </div>
      </Card>
    </div>
  );
};

const ShiftingStepsEditor: React.FC<Props> = ({ initialValue, onChange }) => {
  const [steps, setSteps] = useState<Step[]>(() => {
    const sorted = [...(initialValue.shiftingSteps || [])].sort((a, b) => a.stepOrder - b.stepOrder);
    return sorted.map((s) => ({ id: s.id.toString(), locationLabel: s.locationLabel, "@id": s?.["@id"] }));
  });

  const sensors = useSensors(useSensor(PointerSensor));

  const updateProcessedSteps = (updatedSteps: Step[]) => {
    setSteps(updatedSteps);

    const processed = updatedSteps.map((step, index) => ({
      ...(step['@id'] && { "@id": step['@id'] }),
      //id: step.id,
      stepOrder: index + 1,
      locationLabel: step.locationLabel,
      isFinalPosition: index === updatedSteps.length - 1,
    }));

    onChange(processed);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = steps.findIndex((s) => s.id === active.id);
    const newIndex = steps.findIndex((s) => s.id === over.id);

    updateProcessedSteps(arrayMove(steps, oldIndex, newIndex));
  };

  const updateLabel = (index: number, value: string) => {
    const updated = steps.map((step, i) => i === index ? { ...step, locationLabel: value } : step);
    updateProcessedSteps(updated);
  };

  const deleteStep = (index: number) => {
    const updated = steps.filter((_, i) => i !== index);
    updateProcessedSteps(updated);
  };

  const addStep = () => {
    const updated = [
      ...steps,
      { id: `${Date.now()}`, locationLabel: '' },
    ];
    updateProcessedSteps(updated);
  };

  return (
    <div className={style.step_order__container}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToFirstScrollableAncestor]} onDragEnd={handleDragEnd}>
        <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {steps.map((step, index) => (
            <SortableStep
              key={step.id}
              id={step.id}
              index={index}
              locationLabel={step.locationLabel}
              onChange={(val) => updateLabel(index, val)}
              onDelete={() => deleteStep(index)}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Button type="dashed" onClick={addStep} style={{ marginTop: 12 }}>
        Ajouter une étape
      </Button>
    </div>
  );
};

export default ShiftingStepsEditor;
